import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Loading } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { useAsync } from '../../lib/hooks.js';

export interface StaffIdentity {
  userId: string;
  email: string;
  role: 'superadmin' | 'support';
}

/**
 * Admin shell.
 *
 * The guard here is cosmetic — every `/api/admin/*` endpoint enforces the same
 * check server-side, and returns 404 to a non-staff caller. This just avoids
 * rendering an admin frame around a page that will fail to load.
 */
export function AdminLayout({ children }: { children: (staff: StaffIdentity) => ReactNode }) {
  const whoami = useAsync(() => api.get<StaffIdentity>('/api/admin/whoami'), []);

  if (whoami.loading) return <Loading label="Checking platform access…" />;

  if (whoami.error || !whoami.data) {
    return (
      <div className="container container-narrow">
        <div className="empty">
          <h2>Not available</h2>
          <p className="muted small">
            This area is for platform staff. If you believe you should have access, ask an
            administrator to grant it.
          </p>
        </div>
      </div>
    );
  }

  const staff = whoami.data;
  const canWrite = staff.role === 'superadmin';

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>Platform administration</h1>
          <p>
            Signed in as {staff.email} · <span className="badge">{staff.role}</span>
            {!canWrite && ' — read-only'}
          </p>
        </div>
      </div>

      <nav className="tabs">
        {[
          { to: '/admin/tenants', label: 'Tenants' },
          { to: '/admin/plans', label: 'Plans' },
          { to: '/admin/gateways', label: 'Payment gateways' },
          { to: '/admin/email', label: 'Email' },
          { to: '/admin/releases', label: 'App releases' },
          { to: '/admin/zoom', label: 'Zoom SDK' },
          { to: '/admin/users', label: 'Users' },
          { to: '/admin/audit', label: 'Audit log' },
        ].map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) => `tab${isActive ? ' active' : ''}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {children(staff)}
    </div>
  );
}
